import React, { useRef, useState } from "react";
import { FiChevronUp } from "react-icons/fi";
import FloatingPanel from "../overlays/FloatingPanel";
import Colors from "../colors/colors.component";
import EmojiPicker from "../emoji/emojiPicker";
import "./title-actions.scss";

export default function TitleEditPanel({ anchorRef, kind, record, startAt, canEdit, onSave, onClose }) {
  const project = kind === "project";
  const field = project ? "emojicon" : "color";
  const initial = useRef({ name: record.name || "", appearance: record[field] || (project ? "📋" : "#89afb4") }).current;
  const [name, setName] = useState(initial.name);
  const [appearance, setAppearance] = useState(initial.appearance);
  const [picker, setPicker] = useState(startAt === "appearance");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const saving = useRef(false);
  const icon = useRef(null);
  const closePicker = () => { setPicker(false); icon.current?.focus(); };
  const dirty = name.trim() !== initial.name || appearance !== initial.appearance;
  const disabled = busy || !canEdit || !name.trim() || !dirty;
  const cancel = () => { if (!saving.current) onClose(); };
  const submit = async (event) => {
    event.preventDefault();
    if (event.nativeEvent?.isComposing || saving.current || disabled) return;
    const patch = {};
    if (name.trim() !== initial.name) patch.name = name.trim();
    if (appearance !== initial.appearance) patch[field] = appearance;
    saving.current = true; setBusy(true); setError("");
    try { await onSave(patch); onClose(); }
    catch (err) { setError(err.message || "Could not save. Please try again."); }
    finally { saving.current = false; setBusy(false); }
  };
  return <FloatingPanel modal dismissOnOutside placement="cover" gap={12} anchorRef={anchorRef} width={320} maxHeight={650}
    label={`Edit ${kind}`} onClose={reason => { if (!saving.current) { if (picker && reason !== "outside") closePicker(); else cancel(); } }}
    initialFocus='input[type="text"]' className="ant-title-editor">
    <form onSubmit={submit} onKeyDown={event => {
      if (event.key === "Enter" && event.nativeEvent.isComposing) event.preventDefault();
    }}>
      <div className="ant-title-editor__row">
        <button ref={icon} type="button" className={`ant-title-editor__icon${project ? "" : " ant-title-editor__icon--workspace"}`} disabled={busy || !canEdit}
          style={{ background: project ? record.color || "var(--background2)" : appearance }}
          aria-label={project ? "Change project icon" : "Change workspace color"}
          aria-expanded={picker} onClick={() => setPicker(!picker)}>
          {project ? appearance : (name.trim() || "W").charAt(0)}
        </button>
        <label className="ant-title-editor__name"><span className="ant-title-editor__label">Name</span>
          <input type="text" value={name} onChange={event => setName(event.target.value)}
            disabled={busy || !canEdit} autoComplete="off" required />
        </label>
      </div>
      {picker && <div className="ant-title-editor__picker">
        <button type="button" className="ant-title-editor__hide-picker" disabled={busy}
          aria-expanded="true" onClick={closePicker}>
          <span>pick {project ? "project icon" : "workspace color"}</span><FiChevronUp aria-hidden="true" />
        </button>
        <fieldset disabled={busy || !canEdit}>
          {project ? <EmojiPicker embedded autoFocus={false} onEmojiSelect={emoji => {
            if (!busy && canEdit && emoji?.native) { setAppearance(emoji.native); closePicker(); }
          }} /> : <Colors columns={7} size={32} round={false} stretch customOnChange defColor={appearance}
            returnColor={setAppearance} />}
        </fieldset>
      </div>}
      {!canEdit && <p role="alert">You no longer have permission to edit this {kind}.</p>}
      {error && <p className="ant-title-editor__error" role="alert">{error}</p>}
      <div className="ant-title-editor__actions">
        <button type="button" onClick={cancel} disabled={busy}>Cancel</button>
        <button type="submit" disabled={disabled} aria-busy={busy}>{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </form>
  </FloatingPanel>;
}
